import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { findSvfEntry } from "@/data/svfEntries";
import { VITA_LABELS, VITA_DOCTRINE_HEADING } from "@/data/vitaZeno";
import { CLEANTHES_VITA_INTRO, CLEANTHES_VITA_SECTIONS } from "@/data/vitaCleanthes";
import { useAlLabels } from "@/pages/ancient/common";
import { pickLang } from "@/data/stoicosCards";
import { useSEO } from "@/lib/useSEO";

const svfSlug = (ref) => {
  const m = ref.match(/^SVF I\.(\d+)$/);
  return m ? `svf-1-${m[1]}` : null;
};

/* Anchor chip: links to the individual SVF record once it is published. */
const SvfAnchor = ({ a }) => {
  const slug = svfSlug(a.svf);
  const published = slug && findSvfEntry(slug);
  const cls = "inline-flex items-baseline gap-1.5 border border-white/10 px-2.5 py-1 text-[11px] tracking-[0.08em] text-fog/75";
  const inner = (<><span className="text-gold/90">{a.svf}</span><span className="text-fog/50">·</span><span>{a.source}</span></>);
  return published
    ? <Link to={`/ancient-library/svf/cleanthes/${slug}`} className={cls + " hover:border-gold/50 transition-colors"} data-testid={`vita-anchor-${slug}`}>{inner}</Link>
    : <span className={cls} data-testid={`vita-anchor-${a.svf.replace(/[^\w-]+/g, "-")}`}>{inner}</span>;
};

export default function CleanthesVitaPage() {
  const { lang, labels } = useAlLabels();
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ block: "start" });
  }, [hash]);
  useSEO({
    title: "The Life of Cleanthes — Vita from SVF I.463–481 | StoicOs Ancient Library",
    description: "The life of Cleanthes of Assos reconstructed from the ancient testimonia of SVF I.463–481 (Diogenes Laërtius and others): origins, the water-drawer, character, succession of Zeno, writings and death — with source anchors and reliability notes.",
    canonical: "https://stoicos.ai/ancient-library/svf/cleanthes/vita",
  });
  return (
    <main className="min-h-screen bg-ink text-bone" data-testid="cleanthes-vita-page">
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-24">
        <nav className="text-[11px] tracking-[0.22em] uppercase text-fog/60" data-testid="vita-breadcrumb">
          <Link to="/ancient-library" className="hover:text-gold transition-colors">Ancient Library</Link>
          <span className="mx-2">/</span>
          <Link to="/ancient-library/svf" className="hover:text-gold transition-colors">SVF</Link>
          <span className="mx-2">/</span>
          <Link to="/ancient-library/svf/cleanthes" className="hover:text-gold transition-colors" data-testid="vita-back-to-cleanthes">Cleanthes of Assos</Link>
          <span className="mx-2">/</span>
          <span className="text-gold">Vita</span>
        </nav>

        <header className="mt-10">
          <p className="text-[11px] tracking-[0.3em] uppercase text-gold">SVF I.463–481</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-bone" data-testid="vita-title">{pickLang(CLEANTHES_VITA_INTRO.title, lang)}</h1>
          <p className="mt-6 text-fog/80 font-reading text-base md:text-lg leading-[1.85]" data-testid="vita-intro">{pickLang(CLEANTHES_VITA_INTRO.body, lang)}</p>
        </header>

        {CLEANTHES_VITA_SECTIONS.map((s) => (
          <section key={s.id} id={`vita-${s.id}`} className="mt-16 scroll-mt-28" data-testid={`vita-section-${s.id}`}>
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold">{pickLang(s.title, lang)}</h2>
            {s.labels && s.labels.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {s.labels.map((lb) => (
                  <span key={lb} className="text-[10px] tracking-[0.2em] uppercase text-fog/60 border border-white/10 px-2 py-0.5" data-testid={`vita-label-${s.id}-${lb}`}>
                    {pickLang(VITA_LABELS[lb], lang)}
                  </span>
                ))}
              </div>
            )}
            <p className="mt-5 text-bone/85 font-reading text-base md:text-lg leading-[1.9]">{pickLang(s.body, lang)}</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid={`vita-anchors-${s.id}`}>
              {s.anchors.map((a, i) => <SvfAnchor key={i} a={a} />)}
            </div>
            {s.doctrine && s.doctrine.length > 0 && (
              <div className="mt-4" data-testid={`vita-doctrine-${s.id}`}>
                <p className="text-[10px] tracking-[0.25em] uppercase text-gold/70">{pickLang(VITA_DOCTRINE_HEADING, lang)}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {s.doctrine.map((d) => (
                    <Link
                      key={d.slug}
                      to={`/ancient-library/svf/cleanthes/${d.slug}`}
                      className="inline-flex items-baseline gap-1.5 border border-gold/25 px-2.5 py-1 text-[11px] tracking-[0.08em] text-fog/80 hover:border-gold/60 transition-colors"
                      data-testid={`vita-doctrine-link-${d.slug}`}
                    >
                      <span className="text-gold/90">{d.ref}</span>
                      <span className="text-fog/50">·</span>
                      <span>{pickLang(d.title, lang)}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </section>
        ))}

        <div className="mt-20 pt-8 border-t border-white/10">
          <Link to="/ancient-library/svf/cleanthes" className="text-[11px] tracking-[0.25em] uppercase text-gold hover:text-gold/80 transition-colors" data-testid="vita-footer-back">
            ← Cleanthes of Assos — {labels.entries}
          </Link>
        </div>
      </div>
    </main>
  );
}
